import { AdManagementHelper } from './ad-management.helper';
import { AdManagementError } from './ad-management.error';
import { Http, Headers, RequestOptions } from '@angular/http';
import { Injectable } from '@angular/core';
import { map, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Ad } from 'src/app/models/ad';
import { TransactionType } from 'src/app/models/transactionType';
import { Notification } from 'src/app/models/notification';

@Injectable()
export class AdManagementService {
    private readonly notificationResourceUrl;
    private readonly secretResourceUrl;
    private readonly adsResourceUrl;
    private readonly photosResourceUrl;
    private readonly publicAdsServiceUrl;
    private readonly allAdsServiceUrl;
    private readonly publicApiHeaders;
    private readonly publicRequestOptionArgs;

    constructor(private http: Http) {
        this.notificationResourceUrl = environment.adManagementServiceBaseUrl + 'notifications';
        this.secretResourceUrl = environment.adManagementServiceBaseUrl + 'secret';
        this.adsResourceUrl = environment.adManagementServiceBaseUrl + 'ads';
        this.photosResourceUrl = environment.adManagementServiceBaseUrl + 'photos';
        this.publicAdsServiceUrl = this.adsResourceUrl + '?visibility=public';
        this.allAdsServiceUrl = this.adsResourceUrl + "?visibility=private";
        this.publicApiHeaders = new Headers({ 'Content-Type': 'application/json' });
        this.publicRequestOptionArgs = new RequestOptions({ headers: this.publicApiHeaders });
    }

    private createPrivateRequestOptionArgs(authValue: string): RequestOptions {
        const headers = new Headers(this.publicApiHeaders);
        headers.append(environment.authorizationHeader, authValue);
        return new RequestOptions({ headers: headers });
    }

    private handleError(operation: string, url: string, resource: string) {
        return (error: any) => {
            const errorMessage = `${resource} could not be retrieved`;
            const errorDetails = `error in ${operation}() retrieving ${url}`;
            console.error(`${errorDetails}:`, error);
            return throwError(new AdManagementError(errorMessage));
        };
    }

    // HACK: remove me once the secret mechanism is gone
    public getSecret(authToken: string): Observable<string> {
        return this.http
            .get(
                this.secretResourceUrl,
                this.createPrivateRequestOptionArgs(authToken))
            .pipe(
                map(serializedSecret => serializedSecret.json().secret),
                catchError(
                    this.handleError(
                        'getSecret(authToken: string)',
                        this.secretResourceUrl,
                        'Secret')));
    }

    public getAllAds(secret: string): Observable<Ad[]> {
        return this.http
            .get(
                this.allAdsServiceUrl,
                this.createPrivateRequestOptionArgs(secret))
            .pipe(
                map(AdManagementHelper.ConvertReponseToAdArray),
                catchError(
                    this.handleError(
                        'getAllAds(secret: string)',
                        this.allAdsServiceUrl,
                        'All ads')));
    }

    // FIXME: replace with guenuine lookup API
    public getAd(id: string, secret: string): Observable<Ad> {
        return this.http
            .get(
                this.allAdsServiceUrl,
                this.createPrivateRequestOptionArgs(secret))
            .pipe(
                map(AdManagementHelper.AdLookup(id)),
                catchError(
                    this.handleError(
                        'getAd(id: string, secret: string)',
                        this.allAdsServiceUrl,
                        'A single ad: (' + id + ')')));
    }

    // FIXME: replace with guenuine lookup API
    public getPublicAd(id: string): Observable<Ad> {
        return this.http
            .get(
                this.publicAdsServiceUrl)
            .pipe(
                map(AdManagementHelper.AdLookup(id)),
                catchError(
                    this.handleError(
                        'getPublicAd(id: string)',
                        this.publicAdsServiceUrl,
                        'A single public ad: (' + id + ')')));
    }

    public getfilteredPublicAds(transactiontype: string, estatetype: string): Observable<Ad[]> {
        return this.http
            .get(
                this.publicAdsServiceUrl)
            .pipe(
                map(AdManagementHelper.FilterPublicAds(
                    transactiontype === "Vente" ? TransactionType.Vente : TransactionType.Location,
                    estatetype)),
                catchError(
                    this.handleError(
                        'getfilteredPublicAds(transactiontype: TransactionType, estatetype: string)',
                        this.publicAdsServiceUrl,
                        'All public ads: (' + transactiontype + ', ' + estatetype + ')')));
    }

    public putAd(modifiedAd: Ad, secret: string): Observable<Ad> {
        return this.http
            .put(
                this.adsResourceUrl,
                modifiedAd,
                this.createPrivateRequestOptionArgs(secret))
            .pipe(
                map(ad => Ad.createAdFromJsonObject(ad.json())),
                catchError(
                    this.handleError(
                        'putAd(modifiedAd: Ad, secret: string)',
                        this.adsResourceUrl,
                        'Modify ad: (' + modifiedAd.id + ')')));
    }

    // FIXME: server should not return input when calling DELETE
    public deleteAd(deletedAd: Ad, secret: string): Observable<Ad> {
        const deletionUrl = this.adsResourceUrl + '/' + deletedAd.id;
        return this.http
            .delete(
                deletionUrl,
                this.createPrivateRequestOptionArgs(secret))
            .pipe(
                map(ad => Ad.createAdFromJsonObject(ad.json())),
                catchError(
                    this.handleError(
                        'deleteAd(deletedAd: Ad, secret: string)',
                        deletionUrl,
                        'Delete ad: (' + deletedAd.id + ')')));
    }

    private getPhotoUrl(photoId: string, secret: string): Observable<string> {
        return this.http
            .post(
                this.photosResourceUrl,
                photoId,
                this.createPrivateRequestOptionArgs(secret))
            .pipe(
                map(res => res.json()['url'].toString()),
                catchError(
                    this.handleError(
                        'getPhotoUrl(photoId: string, secret: string)',
                        this.photosResourceUrl,
                        'Create photo url: (' + photoId + ')')));
    }

    private uploadPhoto(photoUrl: string, photoContent: any): Observable<any> {
        return this.http
            .put(
                photoUrl,
                photoContent,
                new RequestOptions({ headers: new Headers({ 'Content-Type': 'image/jpeg' }) }));
    }

    // getPhotoUrl and uploadPhoto mashup
    public createPhoto(photoId: string, photoContent: any, secret: string): Observable<any> {
        return this.getPhotoUrl(photoId, secret)
            .pipe(
                map(
                    photoUrl =>
                        this.uploadPhoto(photoUrl, photoContent)
                ),
                catchError(
                    this.handleError(
                        'createPhoto(photoId: string, photoContent: any, secret: string)',
                        null,
                        'Create photo')));
    }

    public postNotification(notification: Notification): Observable<Notification> {
        return this.http
            .post(
                this.notificationResourceUrl,
                notification,
                this.publicRequestOptionArgs)
            .pipe(
                map(notification => notification.json(),
                    catchError(
                        this.handleError(
                            'postNotification(notification: Notification)',
                            this.notificationResourceUrl,
                            'Notification'))));
    }
}
